import { MetricEdit } from './../model/metricEdit';
import { Component, OnInit } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { MetricService } from 'src/app/core/service/metric.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, GroupDescriptor, filterBy } from '@progress/kendo-data-query';

@Component({
  standalone: false,
  selector: 'app-metric-list',
  templateUrl: './metric-list.component.html',
  styleUrls: ['./metric-list.component.scss']
})
export class MetricListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: MetricEdit;
  public selectedMetric: MetricEdit;
  public filterText = '';
  public metricTypes = null;

  private metricTypeRateBasedText = '';
  private metricTypeCountBasedText = '';

  constructor(private metricService: MetricService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService)
  {
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.metricTypes = this.metricService.metricTypes;
    this.metricTypeRateBasedText = this.metricService.metricTypes[0].name;
    this.metricTypeCountBasedText = this.metricService.metricTypes[1].name;
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const metricId = this.selectedMetric.id;
      this.metricService.deleteMetric(metricId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== metricId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('METRIC-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedMetric = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.metricService.existsMetric(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('METRIC-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.metricService.getMetricById(id).subscribe({
            next: (data) =>
            {
              this.selectedMetric = data as MetricEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new MetricEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.metricService.getMetricById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(metric: MetricEdit): void
  {
    this.metricService.saveMetric(metric, this.isNew, metric.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('METRIC-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.metricService.getMetricList().subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedOn = new Date(d.lastModifiedOn); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
